/**
 * ServiceCatalog: "Available Services" tab content.
 *
 * Renders the live catalog grouped by category (active services from the API),
 * plus a "Custom Request" card that opens the `other` intake. Category order and
 * membership are system-driven, so admin edits appear here directly.
 */
import * as React from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Clock,
  HelpCircle,
  Landmark,
  Receipt,
  Rocket,
  Scale,
  Search,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { MultiSelect } from '@finmin/ui';
import { snakeToKebab, formatPriceRange } from '@finmin/utils';
import { useGroupedServices, type GroupedService } from '@/api/services';
import { useProfile } from '@/api/profile';
import { usePanView } from '@/features/dashboard/pan-view-context';

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

const CATEGORY_ICON: Record<string, LucideIcon> = {
  'Tax Planning': Receipt,
  'Investment Guidance': TrendingUp,
  'Loans & Debts': Landmark,
  Registrations: BadgeCheck,
  'Compliance & Legal': Scale,
  'Other Financial Services': Sparkles,
  Startups: Rocket,
};

// ── Component ─────────────────────────────────────────────────────────────────

export function ServiceCatalog() {
  const { isPending: profileLoading } = useProfile();
  // Settled (success OR error), not just success — a permanently-erroring
  // profile fetch must not gate the catalog forever (falls back to the
  // unfiltered catalog since `profile` stays undefined on error).
  const profileSettled = !profileLoading;
  const { selection, selectedPan } = usePanView();
  const panType = selection === 'all' ? undefined : selectedPan?.pan_type;
  const { data, isLoading } = useGroupedServices(panType, {
    enabled: profileSettled,
  });
  const [query, setQuery] = React.useState('');
  /** Comma-separated selected categories; '' = all. */
  const [categoryFilter, setCategoryFilter] = React.useState('');

  if (!profileSettled || isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-40 rounded-2xl border border-border bg-background animate-pulse" />
        ))}
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const allGroups = data?.groups ?? [];
  const categoryOptions = allGroups.map((g) => ({
    value: g.category,
    label: `${g.category} (${g.services.length})`,
  }));
  const picked = new Set(categoryFilter ? categoryFilter.split(',').filter(Boolean) : []);

  const groups = allGroups
    .filter((group) => picked.size === 0 || picked.has(group.category))
    .map((group) => ({
      ...group,
      services: q
        ? group.services.filter(
            (s) =>
              (s.name ?? s.service_type).toLowerCase().includes(q) ||
              (s.details ?? '').toLowerCase().includes(q) ||
              (s.description ?? '').toLowerCase().includes(q),
          )
        : group.services,
    }))
    .filter((group) => group.services.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services…"
            aria-label="Search services"
            className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="w-full sm:w-60">
          <MultiSelect
            options={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            allLabel="All categories"
            ariaLabel="Filter by category"
            searchable
          />
        </div>
      </div>

      {groups.length === 0 && (q || picked.size > 0) && (
        <p className="text-sm text-muted-foreground">
          No services match your filters. Try a different search or category, or start a custom
          request below.
        </p>
      )}

      {groups.map((group) => {
        const Icon = CATEGORY_ICON[group.category] ?? Sparkles;
        return (
          <section key={group.category} className="space-y-3">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" style={{ color: FOREST }} aria-hidden />
              <h3 className="text-sm font-semibold text-foreground">{group.category}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {group.services.map((service, idx) => (
                <ServiceCard key={service.service_type} service={service} Icon={Icon} index={idx} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4" style={{ color: FOREST }} aria-hidden />
          <h3 className="text-sm font-semibold text-foreground">Something else</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <CustomServiceCard />
        </div>
      </section>
    </div>
  );
}

// ── Service card ──────────────────────────────────────────────────────────────

function ServiceCard({
  service,
  Icon,
  index,
}: {
  service: GroupedService;
  Icon: LucideIcon;
  index: number;
}) {
  const navigate = useNavigate();
  const stagger  = `stagger-${Math.min((index % 8) + 1, 8)}`;

  function handleStart(e: React.MouseEvent) {
    e.preventDefault();
    void navigate({
      to: '/service-request/new/$serviceType',
      params: { serviceType: snakeToKebab(service.service_type) },
    });
  }

  return (
    <div
      className={`group rounded-2xl border border-border bg-background flex flex-col transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/15 animate-slide-up-fade ${stagger}`}
    >
      <div className="px-3.5 pt-3.5 pb-1.5 flex items-center gap-2.5">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: MINT }}
        >
          <Icon className="h-[18px] w-[18px]" style={{ color: FOREST }} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm leading-tight text-foreground truncate">
            {service.name ?? service.service_type}
          </h4>
          {/* Without this, same-named services (ITR Filing Original vs Revised)
              render as identical cards. */}
          {service.details && (
            <p className="text-[11px] leading-tight text-muted-foreground truncate">
              {service.details}
            </p>
          )}
        </div>
      </div>

      <div className="px-3.5 pb-3.5 flex flex-col flex-1 gap-2.5">
        <p className="text-xs text-muted-foreground leading-snug line-clamp-2 flex-1">
          {service.description ?? 'Get expert help with this service.'}
        </p>

        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="font-semibold text-foreground">
            {formatPriceRange(service.price_min, service.price_max)}
          </span>
          {service.expected_tat && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden />
              {service.expected_tat}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleStart}
          className="w-full h-8 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold text-white transition-all active:scale-95 hover:shadow-md group/cta"
          style={{ backgroundColor: AMBER }}
        >
          Start Now
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform group-hover/cta:translate-x-0.5"
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}

// ── Custom (open-ended `other`) card ──────────────────────────────────────────

function CustomServiceCard() {
  const navigate = useNavigate();

  function handleStart(e: React.MouseEvent) {
    e.preventDefault();
    void navigate({
      to: '/service-request/new/$serviceType',
      params: { serviceType: 'other' },
    });
  }

  return (
    <div className="group rounded-2xl border border-border bg-background flex flex-col transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/15">
      <div className="px-3.5 pt-3.5 pb-1.5 flex items-center gap-2.5">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
          style={{ backgroundColor: MINT }}
        >
          <HelpCircle className="h-[18px] w-[18px]" style={{ color: FOREST }} aria-hidden />
        </div>
        <h4 className="flex-1 min-w-0 font-semibold text-sm leading-tight text-foreground truncate">
          Custom Request
        </h4>
      </div>

      <div className="px-3.5 pb-3.5 flex flex-col flex-1 gap-2.5">
        <p className="text-xs text-muted-foreground leading-snug line-clamp-2 flex-1">
          Describe what you need and we'll match you with the right expert
        </p>
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="font-semibold text-foreground">Custom quote</span>
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" aria-hidden />
            1–2 days
          </span>
        </div>
        <button
          type="button"
          onClick={handleStart}
          className="w-full h-8 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold text-white transition-all active:scale-95 hover:shadow-md group/cta"
          style={{ backgroundColor: AMBER }}
        >
          Start Now
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform group-hover/cta:translate-x-0.5"
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
